import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { Reveal } from "@/hooks/useReveal";

type Props = {
  eyebrow?: string;
  heading?: string;
  subheading?: string;
  primaryLabel?: string;
  primaryHref?: string;
  secondaryLabel?: string;
  secondaryHref?: string;
  onPrimaryClick?: () => void;
  onSecondaryClick?: () => void;
  className?: string;
};

/**
 * End-of-page dual-CTA conversion band — primary (apply) + secondary (free scan).
 * Drop into any page to give every visitor an exit ramp.
 */
export const ConversionBand = ({
  eyebrow = "Your Next Step",
  heading = "Stop building from a broken blueprint.",
  subheading = "Pick the entry point that fits where you are right now. Both are designed to compound.",
  primaryLabel = "Apply for Coaching",
  primaryHref = "/inquire?lane=coaching",
  secondaryLabel = "Take the Free Scan",
  secondaryHref = "/scan",
  onPrimaryClick,
  onSecondaryClick,
  className,
}: Props) => (
  <section
    className={cn(
      "relative overflow-hidden py-20 md:py-28 border-t border-border bg-gradient-to-br from-navy-deep via-navy to-navy-deep",
      className,
    )}
  >
    <div
      aria-hidden
      className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,hsl(var(--gold)/0.08),transparent_50%),radial-gradient(circle_at_70%_80%,hsl(var(--orange)/0.06),transparent_50%)]"
    />
    <div className="container relative max-w-4xl text-center">
      <Reveal variant="up">
        <span className="eyebrow !mx-auto inline-flex">
          <Sparkles className="h-3 w-3" /> {eyebrow}
        </span>
        <h2 className="font-display font-bold uppercase text-white text-3xl md:text-5xl mt-5 leading-[1.05]">
          {heading.split(".").map((part, i, arr) => (
            <span key={i}>
              {part}
              {i < arr.length - 1 && part && (
                <span className="text-gradient-gold">.</span>
              )}
              {i === 0 && arr.length > 2 && <br />}
            </span>
          ))}
        </h2>
        <p className="text-silver text-base md:text-lg mt-6 max-w-2xl mx-auto leading-relaxed">
          {subheading}
        </p>

        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to={primaryHref}
            onClick={onPrimaryClick}
            className="group inline-flex items-center justify-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-sm px-8 py-5 shadow-cta hover:brightness-110 transition-all"
          >
            {primaryLabel}
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link
            to={secondaryHref}
            onClick={onSecondaryClick}
            className="inline-flex items-center justify-center gap-2 border border-gold/50 text-white font-display uppercase tracking-[0.2em] text-sm px-8 py-5 hover:bg-gold/10 hover:border-gold transition-all"
          >
            {secondaryLabel}
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);
